import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertOctagon, Info, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEV = {
    high:   { ring: 'border-[#FF3B30]/60 bg-[#FF3B30]/10', accent: 'text-[#FF3B30]', icon: AlertOctagon },
    medium: { ring: 'border-[#FFD700]/40 bg-[#FFD700]/5',  accent: 'text-[#FFD700]', icon: Info },
    low:    { ring: 'border-white/15 bg-white/5',          accent: 'text-[#A0AAB2]', icon: Info },
};

/**
 * Quick sanity-check banner — surfaces today's parity alerts (cash drawer
 * underwater, casino drain, promo budget blown). Dismissible per-session
 * via the X button so the operator can acknowledge.
 */
const ParityAlertsCard = () => {
    const [data, setData] = useState(null);
    const [dismissed, setDismissed] = useState(() => new Set());

    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            try {
                const { data } = await axios.get(`${API}/admin/recon/parity-alerts`);
                if (!cancelled) setData(data);
            } catch { /* ignore */ }
        };
        tick();
        const id = setInterval(tick, 60_000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    const alerts = (data?.alerts || []).filter(a => !dismissed.has(a.code));
    if (alerts.length === 0) return null;

    return (
        <div className="space-y-2" data-testid="parity-alerts">
            {alerts.map((a) => {
                const sty = SEV[a.severity] || SEV.low;
                const Icon = sty.icon;
                return (
                    <div
                        key={a.code}
                        className={`rounded-xl border-2 ${sty.ring} px-4 py-3 flex items-start justify-between gap-3`}
                        data-testid={`parity-alert-${a.code}`}
                    >
                        <div className="flex items-start gap-3 flex-1">
                            <Icon className={`w-5 h-5 ${sty.accent} flex-shrink-0 mt-0.5`} />
                            <div>
                                <p className={`text-[10px] font-black uppercase tracking-widest ${sty.accent}`}>
                                    {a.code.replace(/_/g, ' ')}
                                </p>
                                <p className="text-white text-sm mt-0.5">{a.message}</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setDismissed(prev => new Set([...prev, a.code]))}
                            className="p-1 rounded-md hover:bg-white/5 text-[#A0AAB2] flex-shrink-0"
                            title="Acknowledge"
                            data-testid={`parity-alert-dismiss-${a.code}`}
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

export default ParityAlertsCard;
